/* eslint-disable react/no-children-prop */
/* eslint-disable react/prop-types */
/* eslint-disable no-underscore-dangle */
import React from 'react';

import { Header, Grid, Button, Card, Breadcrumb } from 'semantic-ui-react';

import { graphql } from 'gatsby';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';

import {
  BackgroundImageTeamMember,
  ContactForm,
  InternalLink,
  Location,
  Map,
  OutboundLink,
  SEO,
  BreadcrumbListSchema,
  LocationSchema,
  EVENT_CATEGORIES,
  EVENT_ACTIONS,
  EVENT_LABELS,
  BasicCarousel,
  componentIterator,
  Image,
  SlideReveal,
  multipleComponentIterator,
  useResponsive,
  HoursOfOperation,
  MarkdownContent,
  FadeReveal,
  FishermanIcon,
} from '@bluefin/components';

import { createTeamMemberSlug } from '../../index';
import {
  getPageByType,
  getBookNowPage,
  createLocationName,
  getLocationTeamMembers,
  getBusinessFiles,
  getBusinessFile,
} from '../../template-utils';
import { constructFullPageUrl, ensureSlugSlashes } from '../../utils';

export default function LocationTemplate({ data, pageContext }) {
  const { isDesktop } = useResponsive();

  const {
    fishermanBusinessLocation: {
      _id: locationId,
      name,
      street,
      city,
      state,
      zipCode,
      phoneNumber,
      email,
      featuredImage,
      locationImages = [],
      seoTitle,
      seoDescription,
      hours = [],
    },
    allFishermanBusinessTeam: { nodes: allTeam = [] } = {},
    fishermanBusiness,
    allFishermanBusinessWebsitePage: { nodes: allPages = [] } = {},
    allFishermanBusinessFile: { nodes: allFiles = [] } = {},
    site: {
      siteMetadata: { baseUrl },
    },
  } = data;
  const {
    pageSlug,
    fishermanParentPageTitle = 'Locations',
    fishermanParentPageSlug = '/locations/',
    templateProps = {},
  } = pageContext;
  const {
    destinationEmail,
    destinationEmails,
    teamMembersSectionTitle = 'Featured Team Members',
    teamMembersSectionDescription = '',
    showEmail = true,
    useBusinessNameInMapQuery = true,
  } = templateProps;

  function createPageDescription() {
    return `Discover our prime location in ${city}. Offering a range of services and amenities to cater to your needs. Visit us in ${city} today!`;
  }

  const { name: businessName = '' } = fishermanBusiness || {};

  const locationTeamMembers = getLocationTeamMembers(allTeam, locationId);
  const locationName = createLocationName({ name, street, city });
  const bookingPage = getBookNowPage(allPages);
  const teamPage = getPageByType(allPages, 'Team');
  const { title: teamPageTitle, slug: teamPageSlug } = teamPage || {};

  const locationBusinessFileIds = (locationImages || []).map(({ _id }) => _id);
  let locationBusinessFiles = getBusinessFiles(locationBusinessFileIds, allFiles);
  const featuredBusinessFile = getBusinessFile(featuredImage, allFiles);
  if (!_isNil(featuredBusinessFile)) {
    locationBusinessFiles = [featuredBusinessFile, ...locationBusinessFiles];
  }

  return (
    <>
      <SEO
        businessName={businessName}
        pageName={`${locationName} Location`}
        title={seoTitle}
        description={seoDescription || createPageDescription()}
        images={[featuredBusinessFile]}
      />
      <LocationSchema
        businessName={businessName}
        businessCategories={fishermanBusiness.businessCategories}
        url={constructFullPageUrl(baseUrl, pageSlug)}
        address={{
          streetAddress: street,
          addressLocality: city,
          addressRegion: state,
          postalCode: zipCode,
        }}
        telephone={phoneNumber}
      />
      <BreadcrumbListSchema
        items={[
          {
            name: `${fishermanParentPageTitle} | ${businessName}`,
            url: constructFullPageUrl(baseUrl, ensureSlugSlashes(fishermanParentPageSlug)),
          },
          {
            name: `${locationName} Location | ${businessName}`,
          },
        ]}
      />
      <div className="location-page-container">
        {!_isEmpty(locationBusinessFiles) && (
          <BasicCarousel
            className="location-header hero-carousel"
            size={1}
            grouping={1}
            displayIndicators
            displayPlayPauseButton={false}
            autoScroll={false}
            leftArrowIcon="fancy-arrow-left"
            rightArrowIcon="fancy-arrow-right"
            animation={{
              name: 'blur',
              duration: 200,
              transitionProps: {
                preAnimating: { opacity: 0.99, blur: '2px' },
                animating: { opacity: 0.99, blur: '2px' },
              },
            }}
            items={componentIterator({
              iterator: locationBusinessFiles,
              component: (
                <Image background className="hero-image overlay">
                  <Header as="h1">{locationName}</Header>
                  {bookingPage && (
                    <Button
                      primary
                      as={OutboundLink}
                      to={bookingPage.url}
                      event={{
                        category: EVENT_CATEGORIES.BOOKING_EVENT_CATEGORY,
                        action: EVENT_ACTIONS.BOOKING_SCHEDULE_APPOINTMENT_INTENT_EVENT_ACTION,
                      }}
                    >
                      {bookingPage.title}
                    </Button>
                  )}
                </Image>
              ),
              propMap: { src: 'src', altText: 'altText' },
            })}
          />
        )}
        {_isEmpty(locationBusinessFiles) && (
          <div className="location-header">
            {_isEmpty(locationBusinessFiles) && (
              <Breadcrumb>
                <Breadcrumb.Section
                  link
                  to={ensureSlugSlashes(fishermanParentPageSlug)}
                  as={InternalLink}
                >
                  <FishermanIcon iconName="fancy-arrow-left" />
                  {fishermanParentPageTitle}
                </Breadcrumb.Section>
                <Breadcrumb.Divider content="/" />
                <Breadcrumb.Section>{locationName}</Breadcrumb.Section>
              </Breadcrumb>
            )}
            <div className="location-header-content">
              <Header as="h1">{locationName}</Header>
              {bookingPage && (
                <Button
                  primary
                  as={OutboundLink}
                  to={bookingPage.url}
                  event={{
                    category: EVENT_CATEGORIES.BOOKING_EVENT_CATEGORY,
                    action: EVENT_ACTIONS.BOOKING_SCHEDULE_APPOINTMENT_INTENT_EVENT_ACTION,
                  }}
                >
                  {bookingPage.title}
                </Button>
              )}
            </div>
          </div>
        )}
        <Grid stackable className="location-info-container">
          {!_isEmpty(locationBusinessFiles) && (
            <Grid.Column width={16}>
              <Breadcrumb>
                <Breadcrumb.Section
                  link
                  to={ensureSlugSlashes(fishermanParentPageSlug)}
                  as={InternalLink}
                >
                  <FishermanIcon iconName="fancy-arrow-left" />
                  {fishermanParentPageTitle}
                </Breadcrumb.Section>
                <Breadcrumb.Divider content="/" />
                <Breadcrumb.Section>{locationName}</Breadcrumb.Section>
              </Breadcrumb>
            </Grid.Column>
          )}
          <Grid.Column width={8} className="form-column">
            <FadeReveal triggerOnce duration={1250}>
              <ContactForm
                header={<Header as="h2">Contact Us</Header>}
                buttonText="Submit"
                centeredHeaders={false}
                hideOptIn={false}
                className=""
                fluidButton={false}
                inline={false}
                showLabels
                subheader=""
                termsCopy=""
                withContainer={false}
                locationId={locationId}
                businessId={fishermanBusiness._id}
                businessType={fishermanBusiness.type}
                destinationEmail={destinationEmail}
                destinationEmails={destinationEmails}
                event={{
                  category: EVENT_CATEGORIES.CONTACT_EVENT_CATEGORY,
                  action: EVENT_ACTIONS.CONTACT_SUBMIT_INQUIRY_EVENT_ACTION,
                }}
              />
            </FadeReveal>
          </Grid.Column>
          <Grid.Column width={8} className="info-column">
            <FadeReveal triggerOnce duration={1250} delay={450}>
              <div className="map-section">
                <Map
                  address={{
                    street,
                    city,
                    state,
                    zip: zipCode,
                  }}
                  businessName={useBusinessNameInMapQuery ? businessName : ''}
                />
              </div>
              <div className="info-section">
                <div>
                  <Location
                    address={{
                      street,
                      city,
                      state,
                      zip: zipCode,
                    }}
                    email={showEmail ? email : null}
                    phone={phoneNumber}
                    className="location-information"
                    businessName={useBusinessNameInMapQuery ? businessName : ''}
                    showBusinessName={false}
                  />
                  <HoursOfOperation hours={hours} displayOption="grouped" />
                </div>
              </div>
            </FadeReveal>
          </Grid.Column>
        </Grid>
        {!_isEmpty(locationTeamMembers) && (
          <Grid stackable textAlign="center" className="location-team-container">
            <Grid.Column width={16} textAlign="center">
              <div className="team-section-header">
                <Header as="h2">{teamMembersSectionTitle}</Header>
                <MarkdownContent content={teamMembersSectionDescription} />
              </div>
              <Card.Group itemsPerRow={3} className="location-team-members">
                <SlideReveal
                  cascade
                  triggerOnce
                  className="ui card"
                  fade
                  direction="up"
                  children={multipleComponentIterator({
                    iterator: locationTeamMembers,
                    limit: isDesktop ? 3 : 4,
                    components: [
                      {
                        component: (
                          <BackgroundImageTeamMember
                            useModal={false}
                            as={InternalLink}
                            moreInfoIconName="fancy-arrow-right"
                          />
                        ),
                        propMap: {
                          key: '_id',
                          'teamMember.name': 'name',
                          'teamMember.role': 'role',
                          'teamMember.photo': 'gatsbyImage',
                          'teamMember.description': 'description',
                          'teamMember.email': 'email',
                          'teamMember.phone': 'phone',
                          'teamMember.locations': 'locations',
                          'teamMember.hours': 'hours',
                          to: createTeamMemberSlug,
                        },
                      },
                    ],
                  })}
                />
              </Card.Group>
              {teamPage && (
                <Button
                  to={ensureSlugSlashes(teamPageSlug)}
                  primary
                  as={InternalLink}
                  event={{
                    category: EVENT_CATEGORIES.TEAM_EVENT_CATEGORY,
                    action: EVENT_ACTIONS.TEAM_VIEW_ALL_MEMBERS_EVENT_ACTION,
                    label: EVENT_LABELS.LOCATION_LABEL,
                  }}
                >
                  See all {teamPageTitle}
                </Button>
              )}
            </Grid.Column>
          </Grid>
        )}
      </div>
    </>
  );
}

export const query = graphql`
  query ($id: Int!) {
    site {
      siteMetadata {
        baseUrl
      }
    }
    allFishermanBusinessWebsitePage {
      nodes {
        url
        title
        pageType
        slug
      }
    }
    fishermanBusiness {
      _id
      type
      name
      businessCategories {
        category
      }
      socialMedia {
        link
        type
      }
    }
    fishermanBusinessLocation(_id: { eq: $id }) {
      _id
      city
      country
      email
      hourLabels
      latitude
      longitude
      name
      order
      phoneNumber
      primaryLocation
      state
      street
      timezone
      uuid
      verifiedAddress
      zipCode
      hours {
        close
        day
        label
        location
        open
        openAllDay
        _id
      }
      featuredImage
      locationImages {
        _id
      }
      seoTitle
      seoDescription
    }
    allFishermanBusinessTeam(filter: { show: { eq: true } }) {
      nodes {
        _id
        name
        role
        group
        email
        phone
        description
        image
        gatsbyImage {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
        socialMedia {
          platform
          url
        }
        hours {
          startDay
          openTime
          closeTime
        }
        locations {
          _id
          name
          location
          merchantLocation
        }
        show
      }
    }
    allFishermanBusinessFile {
      nodes {
        _id
        file
        gatsbyFile {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
        description
      }
    }
  }
`;
